import G6 from '@antv/g6'
import type { ProcessChainNode } from '../types'
import { PROCESS_CHAIN_ICON } from './process-chain-icons'
import { getCssColorVar } from '@/utils/chart-theme-colors'

// 卡片样式节点尺寸
const CARD_W = 180
const CARD_H = 56
const CARD_R = 6
const ICON_S = 28
const ICON_PAD = 12 // 图标距卡片左边距

let registered = false

/** 获取主题主色（从 CSS 变量解析实际值） */
function getThemePrimaryColor (): string {
  if (typeof document === 'undefined') return '#409eff'
  return getComputedStyle(document.documentElement)
    .getPropertyValue('--cm-color-primary')
    .trim() || '#409eff'
}

/** 将 SVG data URL 中的填充/描边色替换为主题色，用于选中态图标 */
function recolorSvgDataUrl (dataUrl: string, color: string): string {
  if (!dataUrl) return dataUrl

  let svg: string
  let isBase64 = false
  if (dataUrl.includes(';base64,')) {
    svg = atob(dataUrl.split(';base64,')[1])
    isBase64 = true
  } else if (dataUrl.includes(';charset=utf-8,')) {
    svg = decodeURIComponent(dataUrl.split(';charset=utf-8,')[1])
  } else if (dataUrl.startsWith('data:image/svg+xml,')) {
    svg = decodeURIComponent(dataUrl.split(',')[1])
  } else {
    return dataUrl
  }

  // 替换 fill/stroke 属性值（保留 white/none/transparent 以及 url(...) 渐变引用）
  const recolored = svg.replace(
    /(fill|stroke)="(?!none|transparent|url\(|#fff\b|#ffffff\b|white\b)([^"]*)"/gi,
    `$1="${color}"`
  )

  if (isBase64) {
    return `data:image/svg+xml;base64,${btoa(recolored)}`
  }
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(recolored)}`
}

/** 选中态图标缓存（按 dataUrl+color 去重，避免重复解析 SVG） */
const themedIconCache = new Map<string, string>()

function getThemedIcon (dataUrl: string, color: string): string {
  const key = `${dataUrl}::${color}`
  const cached = themedIconCache.get(key)
  if (cached) return cached
  const themed = recolorSvgDataUrl(dataUrl, color)
  themedIconCache.set(key, themed)
  return themed
}

/** G6 image shape 自定义属性名：存储原始 img 用于取消选中恢复 */
const ORIGINAL_IMG_KEY = 'data-original-img'

type NodeCfg = {
  label?: string
  raw?: ProcessChainNode
  roleLabel?: string
  threatEntityLabel?: string
  /** 事件威胁等级：1-低危 2-中危 3-高危 */
  eventLevel?: number
  /** 是否匹配列表行 business_id，为 true 时在右上角绘制定位图标 */
  isBusinessIdMatch?: boolean
}

type EdgeCfg = {
  edgeLabel?: string
  startPoint?: { x: number; y: number }
  endPoint?: { x: number; y: number }
}

export const PROCESS_CHAIN_EDGE_STYLE = {
  normal: {
    stroke: '#b8bcc4' /* dm-keep */,
    lineWidth: 1.5,
    lineDash: undefined as number[] | undefined,
  },
  keyPath: {
    stroke: '#e54545',
    lineWidth: 1.5,
    lineDash: [6, 4] as number[],
  },
  rootPath: {
    stroke: '#67c23a',
    lineWidth: 2,
    lineDash: undefined as number[] | undefined,
  },
} as const

function edgeArrow (fill: string) {
  return {
    path: G6.Arrow.triangle(5, 6, 0),
    fill,
    d: 0,
  }
}

function drawEdgeLabel (cfg: EdgeCfg, group: any, tagColor?: string) {
  const text = cfg.edgeLabel
  if (!text) return
  const sp = cfg.startPoint!
  const ep = cfg.endPoint!
  const x = (sp.x + ep.x) / 2
  const y = (sp.y + ep.y) / 2
  const width = Math.max(52, text.length * 12 + 20)

  const dx = ep.x - sp.x
  const dy = ep.y - sp.y
  let rotate = Math.atan2(dy, dx)
  if (Math.abs(rotate) > Math.PI / 2) {
    rotate = rotate > 0 ? rotate - Math.PI : rotate + Math.PI
  }

  const bgColor = tagColor || '#303133' /* dm-keep */
  const labelGroup = group.addGroup({ name: 'edge-label-group' })

  labelGroup.addShape('rect', {
    attrs: {
      x: x - width / 2,
      y: y - 10,
      width,
      height: 20,
      radius: 10,
      fill: bgColor,
      stroke: 'transparent',
      lineWidth: 0,
    },
    name: 'edge-action-tag-bg',
  })
  labelGroup.addShape('text', {
    attrs: {
      x,
      y,
      text,
      fontSize: 12,
      fontWeight: 400,
      fill: '#ffffff' /* dm-keep */,
      textAlign: 'center',
      textBaseline: 'middle',
    },
    name: 'edge-action-tag-text',
  })

  labelGroup.rotateAtPoint(x, y, rotate)
}

/** 根据对象类型选择主图标 */
function pickNodeIcon (raw?: ProcessChainNode): string {
  return PROCESS_CHAIN_ICON.object(raw?.object_type)
}

/** 需要在选中态被 recolor 的 image shape 名称 */
const IMAGE_NAMES = ['node-icon', 'network-indicator']

/** 从 group 中收集所有需要替换的图标 shape 的元信息 */
function collectIconShapes (group: any): Array<{ shape: any; name: string; x: number; y: number; w: number; h: number; rawUrl: string }> {
  const result: Array<{ shape: any; name: string; x: number; y: number; w: number; h: number; rawUrl: string }> = []
  for (const name of IMAGE_NAMES) {
    const shape = group.find((el: any) => el.get('name') === name)
    if (shape) collectFromShape(shape, name, result)
  }
  return result
}

function collectFromShape (shape: any, name: string, result: Array<{ shape: any; name: string; x: number; y: number; w: number; h: number; rawUrl: string }>) {
  const rawUrl = shape.attr(ORIGINAL_IMG_KEY)
  if (!rawUrl) return
  result.push({
    shape,
    name,
    x: shape.attr('x'),
    y: shape.attr('y'),
    w: shape.attr('width'),
    h: shape.attr('height'),
    rawUrl,
  })
}

/** 销毁旧图标 shape 并创建新的（G6 Canvas 渲染器下 attr('img') 更新不可靠，需要重建 shape） */
function replaceIconShapes (group: any, items: Array<{ shape: any; name: string; x: number; y: number; w: number; h: number; rawUrl: string }>, imgUrlFn: (rawUrl: string) => string) {
  for (const item of items) {
    item.shape.remove()
    group.addShape('image', {
      attrs: {
        x: item.x,
        y: item.y,
        width: item.w,
        height: item.h,
        img: imgUrlFn(item.rawUrl),
        [ORIGINAL_IMG_KEY]: item.rawUrl,
      },
      name: item.name,
    })
  }
}

/** 选中：应用主题色到 keyShape、label、图标 */
function applySelectionStyle (group: any, themeColor: string) {
  const keyShape = group.find((el: any) => el.get('name') === 'key-shape')
  const label = group.find((el: any) => el.get('name') === 'node-label')
  if (keyShape) {
    keyShape.attr('stroke', themeColor)
    keyShape.attr('shadowColor', 'rgba(64,158,255,0.25)')
    keyShape.attr('shadowBlur', 12)
    keyShape.attr('shadowOffsetX', 0)
    keyShape.attr('shadowOffsetY', 2)
  }
  if (label) {
    label.attr('fill', themeColor)
    label.attr('fontWeight', 600)
  }
  const iconItems = collectIconShapes(group)
  replaceIconShapes(group, iconItems, (rawUrl) => getThemedIcon(rawUrl, themeColor))
}

/** 取消选中：恢复 keyShape、label、图标为原始样式 */
function restoreSelectionStyle (group: any) {
  const keyShape = group.find((el: any) => el.get('name') === 'key-shape')
  const label = group.find((el: any) => el.get('name') === 'node-label')
  if (keyShape) {
    keyShape.attr('stroke', getCssColorVar('--cm-color-border-light', '#e4e7ed'))
    keyShape.attr('shadowColor', 'transparent')
    keyShape.attr('shadowBlur', 0)
    keyShape.attr('shadowOffsetX', 0)
    keyShape.attr('shadowOffsetY', 0)
  }
  if (label) {
    label.attr('fill', getCssColorVar('--cm-color-text-regular', '#303133'))
    label.attr('fontWeight', 500)
  }
  const iconItems = collectIconShapes(group)
  replaceIconShapes(group, iconItems, (rawUrl) => rawUrl)
}

let _graph: any = null

/** 注册边时存储 graph 实例引用，用于在 draw 中查找节点位置 */
export function linkProcessChainGraph (g: any) {
  _graph = g
}

/** 从 edge cfg 中解析节点的中心坐标。
 *  G6 TreeGraph 布局后 cfg.source / cfg.target 为包含 x,y 的模型对象；
 *  若无法直接读取，则通过 graph.findById 回退查找。 */
function resolveNodeCenter (nodeRef: any): { x: number; y: number } | null {
  if (!nodeRef) return null
  // 直接读取模型上的 x, y（compactBox 布局后会写入数据对象）
  if (typeof nodeRef.x === 'number' && typeof nodeRef.y === 'number') {
    return { x: nodeRef.x, y: nodeRef.y }
  }
  // 若 nodeRef 是 G6 Item，取其 model
  if (typeof nodeRef.getModel === 'function') {
    const model = nodeRef.getModel()
    if (model && typeof model.x === 'number' && typeof model.y === 'number') {
      return { x: model.x, y: model.y }
    }
  }
  // 若 source/target 为字符串 ID，通过 graph 回退查找
  if (typeof nodeRef === 'string' && _graph) {
    const item = _graph.findById(nodeRef)
    const model = item?.getModel?.()
    if (model && typeof model.x === 'number' && typeof model.y === 'number') {
      return { x: model.x, y: model.y }
    }
  }
  return null
}

/** 计算 LR 布局下源节点右侧中心 → 目标节点左侧中心的起止点 */
function calcEndpoints (cfg: any) {
  const srcCenter = resolveNodeCenter(cfg.source)
  const tgtCenter = resolveNodeCenter(cfg.target)

  const sp = srcCenter
    ? { x: srcCenter.x + CARD_W / 2, y: srcCenter.y }
    : cfg.startPoint!
  const ep = tgtCenter
    ? { x: tgtCenter.x - CARD_W / 2, y: tgtCenter.y }
    : cfg.endPoint!

  return { sp, ep }
}

export function registerIsoProcessChainShapes () {
  if (registered) return
  registered = true

  G6.registerNode(
    'iso-process-node',
    {
      draw (cfg, group) {
        const raw = (cfg as NodeCfg).raw
        const isThreatEntity = raw?.is_threat_entity === 1
        const isNetwork = raw?.node_role === 'network'
        const label = String((cfg as NodeCfg).label || raw?.name || '')

        const cardX = -CARD_W / 2
        const cardY = -CARD_H / 2

        // 卡片背景色：仅威胁实体 → 浅红，其他 → 白
        const isDanger =  false // isThreatEntity
        const bgFill = isDanger ? '#fff0f0' : getCssColorVar('--cm-color-card-bg', '#ffffff')
        const borderColor = isDanger ? '#e54545' : getCssColorVar('--cm-color-border-light', '#e4e7ed')

        // 卡片主体（key-shape）
        const keyShape = group.addShape('rect', {
          attrs: {
            x: cardX,
            y: cardY,
            width: CARD_W,
            height: CARD_H,
            radius: CARD_R,
            fill: bgFill,
            stroke: borderColor,
            lineWidth: 1,
            cursor: 'pointer',
          },
          name: 'key-shape',
        })

        // 图标（左侧居中）
        const iconUrl = pickNodeIcon(raw)
        const iconX = cardX + ICON_PAD
        const iconY = cardY + (CARD_H - ICON_S) / 2
        group.addShape('image', {
          attrs: {
            x: iconX,
            y: iconY,
            width: ICON_S,
            height: ICON_S,
            img: iconUrl,
            [ORIGINAL_IMG_KEY]: iconUrl,
          },
          name: 'node-icon',
        })

        // 节点名称（图标右侧）
        const displayLabel = label.length > 16 ? `${label.slice(0, 15)}…` : label
        const textX = iconX + ICON_S + 10
        const hasBottomIcons = isThreatEntity || raw?.node_type === 3

        // 角色文本标签（右上角）：发起者用主题色，目标用黄色
        const roleLabel = (cfg as NodeCfg).roleLabel
        if (roleLabel) {
          const isInitiator = raw?.node_type === 2
          const tagColor = isInitiator ? getThemePrimaryColor() : '#e6a23c'
          const tagFontSize = 10
          const tagPaddingX = 6
          const tagPaddingY = 1
          const tagWidth = roleLabel.length * tagFontSize + tagPaddingX * 2
          const tagHeight = tagFontSize + tagPaddingY * 2 + 2
          const tagX = CARD_W / 2 - 0
          const tagBgY = -CARD_H / 2 + 0

          group.addShape('rect', {
            attrs: {
              x: tagX - tagWidth,
              y: tagBgY,
              width: tagWidth,
              height: tagHeight,
              radius: 4,
              fill: tagColor + '1a',
              lineWidth: 1,
            },
            name: 'role-tag-bg',
          })
          group.addShape('text', {
            attrs: {
              x: tagX - tagWidth / 2,
              y: tagBgY + tagHeight / 2,
              text: roleLabel,
              fontSize: tagFontSize,
              fontWeight: 600,
              fill: tagColor,
              textAlign: 'center',
              textBaseline: 'middle',
            },
            name: 'role-tag-text',
          })
        }

        // business_id 匹配标记：左上角绘制定位图标（不随点击变化）
        const isBizMatch = (cfg as NodeCfg).isBusinessIdMatch
        if (isBizMatch) {
          const pinSize = 18
          const pinX = -CARD_W / 2 + pinSize / 2 - 12
          const pinY = -CARD_H / 2 - pinSize / 2 + 0
          // 圆形背景
          group.addShape('circle', {
            attrs: {
              x: pinX + pinSize / 2,
              y: pinY + pinSize / 2,
              r: pinSize / 2,
              fill: '#fff' /* dm-keep */,
              stroke: '#fff' /* dm-keep */,
              lineWidth: 2,
              shadowColor: 'rgba(103, 194, 58, 0.3)',
              shadowBlur: 4,
            },
            name: 'biz-match-bg',
          })
          // 对勾图标
          group.addShape('image', {
            attrs: {
              x: pinX + pinSize / 10,
              y: pinY + pinSize / 10,
              fontSize: 9,
              fontWeight: 700,
              textAlign: 'center',
              textBaseline: 'middle',
              img: PROCESS_CHAIN_ICON.location,
            },
            name: 'biz-match-check',
          })
        }

        // 网络节点：在标签左侧加小地球图标
        if (isNetwork) {
          group.addShape('image', {
            attrs: {
              x: textX,
              y: -6,
              width: 12,
              height: 12,
              img: PROCESS_CHAIN_ICON.network,
              [ORIGINAL_IMG_KEY]: PROCESS_CHAIN_ICON.network,
            },
            name: 'network-indicator',
          })
        }

        // 有底部图标时文本偏上，无则垂直居中
        const labelY = hasBottomIcons ? -7 : 0
        const labelX = isNetwork ? textX + 16 : textX
        group.addShape('text', {
          attrs: {
            x: labelX,
            y: labelY,
            text: displayLabel,
            fontSize: 12,
            fontWeight: 500,
            fill: getCssColorVar('--cm-color-text-regular', '#303133'),
            textAlign: 'left',
            textBaseline: 'middle',
          },
          name: 'node-label',
        })

        // 底部图标：威胁实体图标 + 威胁等级图标
        if (hasBottomIcons) {
          let iconX = textX
          const iconGap = 4

          // 威胁实体图标（红色 virus/bug，不参与选中变色）
          if (isThreatEntity) {
            group.addShape('image', {
              attrs: {
                x: iconX,
                y: 4,
                width: 14,
                height: 14,
                img: PROCESS_CHAIN_ICON.thread,
              },
              name: 'threat-indicator',
            })
            iconX += 14 + iconGap
          }

          // 威胁等级图标（按等级着色：高危红、中危橙、低危主题色）
          if (raw?.node_type === 3) {
            const lv = (cfg as NodeCfg).eventLevel
            const levelUrl = PROCESS_CHAIN_ICON.level(lv, getThemePrimaryColor())
            group.addShape('image', {
              attrs: {
                x: iconX,
                y: 4,
                width: 14,
                height: 14,
                img: levelUrl,
              },
              name: 'level-indicator',
            })
          }
        }

        // 更多按钮：top 为 1（有上级节点）或 3（有下级节点）时在右侧显示三点图标，点击展开关联节点
        if ([1, 3].includes(raw?.top ?? 0)) {
          const moreBtnX = CARD_W / 2 - 16
          const moreBtnY = CARD_H / 2 - 18
          const moreBtnR = 9

          group.addShape('circle', {
            attrs: {
              x: moreBtnX,
              y: moreBtnY,
              r: moreBtnR,
              fill: getCssColorVar('--cm-color-hover-bg', '#f5f7fa'),
              stroke: getCssColorVar('--cm-color-border-base', '#dcdfe6'),
              lineWidth: 1,
              cursor: 'pointer',
            },
            name: 'more-btn-bg',
          })
          group.addShape('image', {
            attrs: {
              x: moreBtnX - 8,
              y: moreBtnY - 8,
              width: 16,
              height: 16,
              img: PROCESS_CHAIN_ICON.more,
              cursor: 'pointer',
            },
            name: 'more-btn-icon',
          })
        }

        return keyShape
      },

      /** 选中态：主题色边框 + 文本高亮 + 阴影 + 图标主题化 */
      setState (name, value, item) {
        if (name !== 'selected') return
        const group = item.getContainer()
        if (value) {
          applySelectionStyle(group, getThemePrimaryColor())
        } else {
          restoreSelectionStyle(group)
        }
      },
    },
    'single-node'
  )

  // 边连接点强制对齐到节点左侧中心 / 右侧中心
  G6.registerEdge('iso-tree-edge', {
    draw (cfg, group) {
      const { sp, ep } = calcEndpoints(cfg)
      const dx = ep.x - sp.x
      const cpOffset = Math.abs(dx) * 0.5
      const path = [
        ['M', sp.x, sp.y],
        ['C', sp.x + cpOffset, sp.y, ep.x - cpOffset, ep.y, ep.x, ep.y],
      ]
      const style = PROCESS_CHAIN_EDGE_STYLE.normal
      const edge = group.addShape('path', {
        attrs: {
          path,
          stroke: style.stroke,
          lineWidth: style.lineWidth,
          endArrow: edgeArrow(style.stroke),
        },
        name: 'edge-path',
      })
      drawEdgeLabel({ ...cfg, startPoint: sp, endPoint: ep } as EdgeCfg, group)
      return edge
    },
  })

  G6.registerEdge('iso-threat-edge', {
    draw (cfg, group) {
      const { sp, ep } = calcEndpoints(cfg)
      const dx = ep.x - sp.x
      const cpOffset = Math.abs(dx) * 0.5
      const style = PROCESS_CHAIN_EDGE_STYLE.keyPath
      const edge = group.addShape('path', {
        attrs: {
          path: [
            ['M', sp.x, sp.y],
            ['C', sp.x + cpOffset, sp.y, ep.x - cpOffset, ep.y, ep.x, ep.y],
          ],
          stroke: style.stroke,
          lineWidth: style.lineWidth,
          lineDash: style.lineDash,
          endArrow: edgeArrow(style.stroke),
        },
        name: 'threat-edge-path',
      })
      drawEdgeLabel({ ...cfg, startPoint: sp, endPoint: ep } as EdgeCfg, group, '#e54545')
      return edge
    },
  })

  // 高亮节点到根节点的路径连边（黄色，优先级最高；tag 颜色由 cfg.tagColor 决定，不因连线变色）
  G6.registerEdge('iso-root-path-edge', {
    draw (cfg: any, group: any) {
      const { sp, ep } = calcEndpoints(cfg)
      const dx = ep.x - sp.x
      const cpOffset = Math.abs(dx) * 0.5
      const style = PROCESS_CHAIN_EDGE_STYLE.rootPath
      const edge = group.addShape('path', {
        attrs: {
          path: [
            ['M', sp.x, sp.y],
            ['C', sp.x + cpOffset, sp.y, ep.x - cpOffset, ep.y, ep.x, ep.y],
          ],
          stroke: style.stroke,
          lineWidth: style.lineWidth,
          lineDash: style.lineDash,
          endArrow: edgeArrow(style.stroke),
          shadowColor: 'rgba(103, 194, 58, 0.35)',
          shadowBlur: 8,
          shadowOffsetX: 0,
          shadowOffsetY: 0,
        },
        name: 'root-path-edge',
      })
      // tagColor 由调用方通过 cfg 传入，保持原始 tag 颜色不变
      const tagColor = cfg.tagColor || undefined
      drawEdgeLabel({ ...cfg, startPoint: sp, endPoint: ep } as EdgeCfg, group, tagColor)
      return edge
    },
  })
}

export const ISO_NODE_SIZE: [number, number] = [CARD_W, CARD_H]
